import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Form, Spinner, Tab, Table, Tabs } from "react-bootstrap";
import { useSearchParams } from "react-router";
import { apiErrorMessage, useGet, useGetLazy, usePost } from "../../api";
import { EmptyState, FilterBar, FilterField, PageHeader, PageToolbar, ToolbarButton } from "../../components/ui";
import { usePermissions } from "../../hooks";
import {
  IPartnerConfigurationHistoryEntry,
  IPartnerConfigurationResponse,
  IPartnerConfigurationSnapshot,
  IPartnerConfigurationSnapshotSummary,
  PARTNER_CONFIGURATION_ALL,
  PartnerConfigurationSnapshotReason,
  PermissionAction,
  PermissionResource,
} from "../../interfaces";
import { ChangeSummary, OP_BADGE } from "./configuration-dialogs";
import { countryName, toStorageExplorerCsv } from "./configuration-model";

const REASONS: Record<PartnerConfigurationSnapshotReason, { label: string; bg: string; hint: string }> = {
  initial: { label: "First pull", bg: "secondary", hint: "The first copy taken of the table." },
  observed: { label: "Changed outside this panel", bg: "warning", hint: "The table differed from the last copy without a save here, e.g. a Storage Explorer upload." },
  "before-save": { label: "Before save", bg: "info", hint: "The table exactly as it stood before a save from this panel." },
  "after-save": { label: "After save", bg: "success", hint: "The table right after a save from this panel." },
  manual: { label: "Manual", bg: "dark", hint: "Taken on request." },
};

interface Filters {
  partitionKey: string;
  changedBy: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { partitionKey: "", changedBy: "", from: "", to: "" };

const download = (name: string, csv: string) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
};

const SnapshotDiff: React.FC<{ id: string; partnerLabel: (pk: string) => string }> = ({ id, partnerLabel }) => {
  const { data, loading, error } = useGet<IPartnerConfigurationSnapshot>(`partners/configuration/snapshots/${id}`);
  if (loading) return <Spinner animation="border" size="sm" />;
  if (error) return <Alert variant="danger">{apiErrorMessage(error)}</Alert>;
  if (!data?.diff.length) return <div className="small text-muted">No differences from the copy before it.</div>;
  return (
    <Table size="sm" className="align-middle mb-0">
      <tbody>
        {data.diff.map((d) => (
          <tr key={`${d.partitionKey}|${d.rowKey}`}>
            <td className="small">{partnerLabel(d.partitionKey)}</td>
            <td className="small text-nowrap">
              <strong>{d.rowKey}</strong> <span className="text-muted">{countryName(d.rowKey)}</span>
            </td>
            <td>
              <Badge bg={OP_BADGE[d.op].bg}>{OP_BADGE[d.op].label}</Badge>
            </td>
            <td>
              <ChangeSummary before={d.before} after={d.after} rowKey={d.rowKey} />
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  );
};

export const PartnerConfigurationHistoryPage: React.FC = () => {
  const { can, isAdmin } = usePermissions();
  const canEdit = isAdmin || can(PermissionAction.UpdateAny, PermissionResource.Partners);
  const [params] = useSearchParams();
  const initialPartner = params.get("partner") ?? "";

  const [draft, setDraft] = useState<Filters>({ ...EMPTY_FILTERS, partitionKey: initialPartner });
  const [filters, setFilters] = useState<Filters>(draft);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ variant: string; text: string } | null>(null);

  // Loading the configuration also snapshots the table if it changed outside the panel.
  const { data: config, error: configError } = useGet<IPartnerConfigurationResponse>("partners/configuration");
  const configured = !!config?.configured;

  const historyUrl = useMemo(() => {
    const q = new URLSearchParams();
    if (filters.partitionKey) q.set("partitionKey", filters.partitionKey);
    if (filters.changedBy) q.set("changedBy", filters.changedBy);
    if (filters.from) q.set("from", new Date(`${filters.from}T00:00:00`).toISOString());
    if (filters.to) q.set("to", new Date(`${filters.to}T23:59:59`).toISOString());
    return `partners/configuration/history?${q.toString()}`;
  }, [filters]);
  const changes = useGet<IPartnerConfigurationHistoryEntry[]>(configured ? historyUrl : null);
  const snapshots = useGet<IPartnerConfigurationSnapshotSummary[]>(configured ? "partners/configuration/snapshots?limit=500" : null);
  const { execute: fetchSnapshot } = useGetLazy<IPartnerConfigurationSnapshot>();
  const { execute: takeSnapshot, loading: taking } = usePost<IPartnerConfigurationSnapshotSummary, Record<string, never>>();

  const names = useMemo(() => new Map((config?.partners ?? []).map((p) => [p.partnerId.toLowerCase(), p.name])), [config]);
  const partnerLabel = (pk: string) => (pk === PARTNER_CONFIGURATION_ALL ? "All partners" : names.get(pk) ?? pk);
  const partnerOptions = useMemo(() => {
    const pks = new Set<string>([PARTNER_CONFIGURATION_ALL, ...(config?.rows ?? []).map((r) => r.partitionKey)]);
    if (initialPartner) pks.add(initialPartner);
    return Array.from(pks).sort((a, b) => partnerLabel(a).localeCompare(partnerLabel(b)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, names, initialPartner]);

  const beforeSaveByBatch = useMemo(
    () => new Map((snapshots.data ?? []).filter((s) => s.reason === "before-save" && s.batchId).map((s) => [s.batchId!, s])),
    [snapshots.data]
  );

  const saves = useMemo(() => {
    const groups = new Map<string, IPartnerConfigurationHistoryEntry[]>();
    for (const e of changes.data ?? []) {
      const key = e.batchId ?? e._id;
      groups.set(key, [...(groups.get(key) ?? []), e]);
    }
    return Array.from(groups.entries()).map(([key, entries]) => ({ key, batchId: entries[0].batchId, entries }));
  }, [changes.data]);

  const downloadSnapshot = async (s: IPartnerConfigurationSnapshotSummary) => {
    try {
      const full = await fetchSnapshot(`partners/configuration/snapshots/${s._id}`);
      if (full) download(`${s.table}-${s.takenAt.slice(0, 19).replace(/[:T]/g, "-")}.csv`, toStorageExplorerCsv(full.rows));
    } catch (err) {
      setNotice({ variant: "danger", text: apiErrorMessage(err) });
    }
  };

  const snapshotNow = async () => {
    try {
      const s = await takeSnapshot("partners/configuration/snapshots", {});
      setNotice({ variant: "success", text: `Saved a copy of ${s.rowCount} rows.` });
      snapshots.refetch().catch(() => undefined);
    } catch (err) {
      setNotice({ variant: "danger", text: apiErrorMessage(err) });
    }
  };

  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <div>
      <PageHeader
        title="Partner configuration history"
        description="Who changed which partner's configuration and when, plus a full copy of the table before and after every save. Copies are also taken when the table changes outside this panel."
      />
      {config && !configured && (
        <EmptyState
          title="Partner configuration isn't available here"
          message="This environment isn't connected to the partner configuration table, so there is no history to show."
          icon="bi-plug"
        />
      )}
      {configError && <Alert variant="danger">{apiErrorMessage(configError)}</Alert>}

      {configured && (
        <>
          <PageToolbar>
            <ToolbarButton label="Configuration" icon="bi-arrow-left" to="/partners/configuration" hint="Back to editing partner configuration" />
            {canEdit && (
              <ToolbarButton
                label="Take snapshot"
                icon="bi-camera"
                onClick={snapshotNow}
                disabled={taking}
                hint="Save a full copy of the table as it is right now"
              />
            )}
          </PageToolbar>
          {notice && (
            <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)}>
              {notice.text}
            </Alert>
          )}

          <Tabs defaultActiveKey="changes" className="mb-3">
            <Tab eventKey="changes" title="Changes">
              <FilterBar
                onSearch={() => setFilters(draft)}
                onClear={() => {
                  setDraft(EMPTY_FILTERS);
                  setFilters(EMPTY_FILTERS);
                }}
                activeCount={activeFilters}
                summary={changes.data ? `${changes.data.length} changes in ${saves.length} saves` : undefined}
              >
                <FilterField label="Partner" hint="Only changes to this partner's rows" md={4}>
                  <Form.Select size="sm" value={draft.partitionKey} onChange={(e) => setDraft({ ...draft, partitionKey: e.target.value })}>
                    <option value="">Any partner</option>
                    {partnerOptions.map((pk) => (
                      <option key={pk} value={pk}>
                        {partnerLabel(pk)}
                      </option>
                    ))}
                  </Form.Select>
                </FilterField>
                <FilterField label="Changed by" hint="Part of the person's email" md={3}>
                  <Form.Control size="sm" value={draft.changedBy} onChange={(e) => setDraft({ ...draft, changedBy: e.target.value })} />
                </FilterField>
                <FilterField label="From" hint="Saved on or after" md={2}>
                  <Form.Control size="sm" type="date" value={draft.from} onChange={(e) => setDraft({ ...draft, from: e.target.value })} />
                </FilterField>
                <FilterField label="To" hint="Saved on or before" md={2}>
                  <Form.Control size="sm" type="date" value={draft.to} onChange={(e) => setDraft({ ...draft, to: e.target.value })} />
                </FilterField>
              </FilterBar>

              {changes.error && <Alert variant="danger">{apiErrorMessage(changes.error)}</Alert>}
              {changes.loading && !changes.data && <Spinner animation="border" size="sm" />}
              {changes.data && !changes.data.length && (
                <EmptyState
                  title="No saved changes"
                  message="Nothing matching has been saved from the configuration panel. Edits made elsewhere show up under Snapshots."
                  icon="bi-clock-history"
                />
              )}
              {saves.map((g) => {
                const first = g.entries[0];
                const before = g.batchId ? beforeSaveByBatch.get(g.batchId) : undefined;
                return (
                  <Card key={g.key} className="mb-3">
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2 py-2">
                      <span className="small">
                        <strong>{new Date(first.changedAt).toLocaleString()}</strong> · {first.changedBy ?? "unknown"} · {g.entries.length}{" "}
                        {g.entries.length === 1 ? "change" : "changes"}
                      </span>
                      {before && (
                        <Button size="sm" variant="outline-secondary" onClick={() => downloadSnapshot(before)} title="The full table as it stood just before this save, in Storage Explorer's import format">
                          Download table before this save
                        </Button>
                      )}
                    </Card.Header>
                    <Table size="sm" className="align-middle mb-0">
                      <tbody>
                        {g.entries.map((e) => (
                          <tr key={e._id}>
                            <td className="small">{partnerLabel(e.partitionKey)}</td>
                            <td className="small text-nowrap">
                              <strong>{e.rowKey}</strong> <span className="text-muted">{countryName(e.rowKey)}</span>
                            </td>
                            <td>
                              <Badge bg={OP_BADGE[e.op].bg}>{OP_BADGE[e.op].label}</Badge>
                            </td>
                            <td>
                              <ChangeSummary before={e.before} after={e.after} rowKey={e.rowKey} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card>
                );
              })}
            </Tab>

            <Tab eventKey="snapshots" title="Snapshots">
              <p className="small text-muted">
                Full copies of <code>{config?.table}</code>. Each one lists what differs from the copy before it, so changes made outside this panel show up
                here too. Download any of them to restore it with Storage Explorer.
              </p>
              {snapshots.error && <Alert variant="danger">{apiErrorMessage(snapshots.error)}</Alert>}
              {snapshots.loading && !snapshots.data && <Spinner animation="border" size="sm" />}
              {snapshots.data && !snapshots.data.length && <EmptyState title="No snapshots yet" message="The first one is taken when the configuration page loads." icon="bi-camera" />}
              {snapshots.data && snapshots.data.length > 0 && (
                <Table size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <th>Taken</th>
                      <th>Why</th>
                      <th>By</th>
                      <th className="text-end">Rows</th>
                      <th>Since previous copy</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {snapshots.data.map((s) => {
                      const reason = REASONS[s.reason];
                      const diffs = s.added + s.changed + s.removed;
                      return (
                        <React.Fragment key={s._id}>
                          <tr>
                            <td className="small text-nowrap">{new Date(s.takenAt).toLocaleString()}</td>
                            <td>
                              <Badge bg={reason.bg} text={reason.bg === "warning" ? "dark" : undefined} title={reason.hint}>
                                {reason.label}
                              </Badge>
                            </td>
                            <td className="small">{s.takenBy ?? <span className="text-muted">—</span>}</td>
                            <td className="small text-end">{s.rowCount}</td>
                            <td className="small">
                              {diffs ? (
                                <span className="d-flex gap-1">
                                  {s.added > 0 && <Badge bg="success">+{s.added}</Badge>}
                                  {s.changed > 0 && <Badge bg="primary">~{s.changed}</Badge>}
                                  {s.removed > 0 && <Badge bg="danger">−{s.removed}</Badge>}
                                </span>
                              ) : (
                                <span className="text-muted">No differences</span>
                              )}
                            </td>
                            <td className="text-end text-nowrap">
                              {diffs > 0 && (
                                <Button size="sm" variant="link" onClick={() => setExpanded(expanded === s._id ? null : s._id)} title="List the rows that differ from the copy before">
                                  {expanded === s._id ? "Hide changes" : "View changes"}
                                </Button>
                              )}
                              <Button size="sm" variant="link" onClick={() => downloadSnapshot(s)} title="This copy in Storage Explorer's import format">
                                Download CSV
                              </Button>
                            </td>
                          </tr>
                          {expanded === s._id && (
                            <tr>
                              <td colSpan={6} className="bg-body-tertiary">
                                <SnapshotDiff id={s._id} partnerLabel={partnerLabel} />
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Tab>
          </Tabs>
        </>
      )}
    </div>
  );
};
